import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import Joi from "joi-browser";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RequestorInformationForm.css";
import { emailPattern, phonePattern, zipCodePattern } from "../../lookups/formPatterns";
import useAddressSuggestions from "../../hooks/useAddressSuggestions";
import Cookies from "js-cookie";

const schema = {
  name: Joi.string().trim().required().label("Name"),
  company: Joi.string().trim().required().label("Company"),
  email: Joi.string()
    .trim()
    .email()
    .regex(emailPattern)
    .required()
    .label("E-mail Address")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid E-mail Address";
        }
      });
      return errors;
    }),
  title: Joi.string().trim().optional().allow("").label("Title"),
  address: Joi.string().trim().required().max(255).label("Bill to Address"),
  city: Joi.string().trim().required().max(255).label("City"),
  state: Joi.string()
    .trim()
    .required()
    .length(2)
    .label("State"),
  zip: Joi.string()
    .required()
    .regex(zipCodePattern)
    .length(5)
    .label("Zip")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Zip Code!";
        }
      });
      return errors;
    }),
  phoneNumber: Joi.string()
    .trim()
    .regex(phonePattern)
    .required()
    .label("Phone Number")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Phone Number";
        }
      });
      return errors;
    }),
  claimNumber: Joi.string().trim().required().label("Claim Number"),
  insuredClient: Joi.string().trim().required().label("Insured/Client"),
  billToPhone: Joi.string()
    .trim()
    .regex(phonePattern)
    .optional()
    .allow("")
    .label("Bill to Phone Number")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Phone Number";
        }
      });
      return errors;
    }),
  billToEmail: Joi.string()
    .trim()
    .email()
    .regex(emailPattern)
    .optional()
    .allow("")
    .label("Bill to E-Mail Address")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid E-mail Address";
        }
      });
      return errors;
    }),
  other: Joi.string().trim().optional().allow("").label("Other"),
};

const RequestorInformationForm = React.forwardRef(
  ({ formData, isAddressInvalid, setFormData }, ref) => {
    const {
      fetchAddressSuggestions,
      setAddressSuggestions,
      isChoosedSuggestion,
      setIsChoosedSuggestion,
      renderSuggestionsDropdown,
    } = useAddressSuggestions();

    const handleChange = async (e) => {
      const { name, value } = e.target;
      setFormData((prevData) => ({
        ...prevData,
        requestorInformation: {
          ...prevData.requestorInformation,
          [name]: name === "state" ? value.toUpperCase() : value,
        },
      }));
      
      if (name === "address") {
        setIsChoosedSuggestion(false);
      }
    };

    const handleSelectAddressSuggestion = (suggestion) => {
      const displayAddress = suggestion.street_line;
      const displayState = suggestion.state;
      const displayCity = suggestion.city;
      const displayZipCode = suggestion.zipcode;

      setFormData((prevData) => ({
        ...prevData,
        requestorInformation: {
          ...prevData.requestorInformation,
          address: displayAddress,
          state: displayState,
          city: displayCity,
          zip: displayZipCode,
        },
      }));
      setIsChoosedSuggestion(true);
      setAddressSuggestions([]);
    };

    useEffect(() => {
      if (formData.address === "" || formData.address?.trim().length < 1) {
        setFormData((prevData) => ({
          ...prevData,
          requestorInformation: {
            ...prevData.requestorInformation,
            state: "",
            city: "",
            zip: "",
          },
        }));
        setAddressSuggestions([]);
      }

      const searchTimeoutId = setTimeout(async () => {
        if (formData.address.length > 2 && !isChoosedSuggestion) {
          await fetchAddressSuggestions(formData.address);
        } else {
          setAddressSuggestions([]);
        }
      }, 500);

      return () => clearTimeout(searchTimeoutId);
    }, [formData.address, isChoosedSuggestion]);

    const [formErrors, setFormErrors] = useState({});

    const validateForm = () => {
      const result = Joi.validate(formData, schema, {
        abortEarly: false,
      });
      if (!result.error) return null;
      let errors = {};
      result.error?.details.forEach((item) => {
        errors[item.path[0]] = item.message;
      });

      // Show error if the Requestor address and the Claimant address are the same
      if (isAddressInvalid) {
        errors = {...errors, address: "The address information for Requestor and Claimant cannot be the same"}
      }

      return errors;
    };

    const validateField = (name) => {
      const result = Joi.validate(formData[name], schema[name], {
        abortEarly: false,
      });
      
      if (result.error) {
        const err = result.error.details[0].message
        setFormErrors(prevState => ({...prevState, [name]: err}))
      } else {
        setFormErrors(prevState => ({...prevState, [name]: ""}))
      }

      // Show error if the Requestor address and the Claimant address are the same
      if (isAddressInvalid) {
        setFormErrors(prevState => ({...prevState, address: "The address information for Requestor and Claimant cannot be the same"}))
      } else {
        setFormErrors(prevState => ({...prevState, address: ""}))
      }
    };

    // Realtime form validation
    const prevFormDataState = useRef()
    useEffect(() => {
      if (prevFormDataState.current) {
        const changedKeys = Object.keys(formData).filter(
          key => prevFormDataState.current[key] !== formData[key]
        );

        if (changedKeys.length > 0) {
          for (let i = 0; i < changedKeys.length; i++) {
            validateField(changedKeys[i])
          }
        }
      }
      prevFormDataState.current = formData;
      
      // Show error if the Requestor address and the Claimant address are the same
      if (isAddressInvalid) {
        setFormErrors(prevState => ({...prevState, address: "The address information for Requestor and Claimant cannot be the same"}))
      } else {
        setFormErrors(prevState => ({...prevState, address: ""}))
      }
    }, [formData]);

    const handleSubmit = (e) => {
      const errors = validateForm();
      setFormErrors(errors || {});

      errors && document.getElementById(`labelOf${Object.keys(errors)[0]}`)?.scrollIntoView({behavior: "smooth"});
      return !errors;
    };
    
    const getFormStatus = () => {
      const errors = validateForm();
      return !errors;
    };

    React.useImperativeHandle(ref, () => ({
      handleSubmit: handleSubmit,
      getFormStatus: getFormStatus,
    }));

    useEffect(() => {
      const fieldsToLoad = [
        "name",
        "company",
        "email",
        "title",
        "address",
        "city",
        "state",
        "zip",
        "phoneNumber",
      ];
      const initialFormData = {};

      fieldsToLoad.forEach((field) => {
        const savedValue = Cookies.get(field);
        if (savedValue) {
          initialFormData[field] = savedValue;
        }
      });
      setFormData((prevData) => ({
          ...prevData,
          requestorInformation: {
            ...prevData.requestorInformation,
            ...initialFormData
          },
        }));
    }, []);

    const renderInput = (
      name,
      placeholder,
      type = "text",
      colSize = 6,
      required = true,
      disabled = false
    ) => (
      <Col md={colSize} className="mb-3">
        <Form.Group
          controlId={`form${name.charAt(0).toUpperCase() + name.slice(1)}`}
          className={`required-wrapper ${required ? "required-field" : ""}`}
        >
          <div className="form-control-wrapper">
            <Form.Label id={`labelOf${name}`}>{placeholder}</Form.Label>
            {type === "textarea" ? (
              <Form.Control
                as="textarea"
                rows={5}
                placeholder={`${required ? "*" : ""}`}
                value={formData[name]}
                name={name}
                disabled={disabled}
                onChange={handleChange}
                onBlur={() => validateField(name)}
                className={`form-control form-control-custom asterisk ${
                  formErrors[name] ? "is-invalid" : ""
                }`}
              />
            ) : (
              <Form.Control
                type={type}
                value={formData[name]}
                name={name}
                disabled={disabled}
                onChange={handleChange}
                onBlur={() => {
                  validateField(name)
                  setTimeout(() => {
                    setAddressSuggestions([])
                  }, 250)
                }
                }
                className={`form-control form-control-custom ${
                  formErrors[name] ? "is-invalid" : ""
                }`}
              />
            )}
            {name === "address" &&
              renderSuggestionsDropdown(handleSelectAddressSuggestion)}
            {required && <span className="red-asterisk">*</span>}
            {formErrors[name] && (
              <Form.Control.Feedback type="invalid">
                {formErrors[name]}
              </Form.Control.Feedback>
            )}
          </div>
        </Form.Group>
      </Col>
    );
    return (
      <Container className="requestor-information-form">
        <h1 className="txt-orange">Requestor Information</h1>
        <p className="p-style">
          Complete the following fields. Fields with an * are required.
        </p>
        <Form ref={ref}>
          <Row>
            {renderInput("name", "Your Name")}
            {renderInput("company", "Your Company")}
            {renderInput("email", "Your E-mail Address", "email")}
            {renderInput("title", "Title", "text", 6, false)}
            {renderInput("address", "Bill to Address")}
            {renderInput("city", "City", "text", 6)}
            {renderInput("state", "State", "text", 6)}
            {renderInput("zip", "Zip", "text", 6)}
            {renderInput("phoneNumber", "Your Phone Number", "tel")}
            {renderInput("claimNumber", "Claim#")}
            {renderInput("insuredClient", "Insured/Client")}
            {renderInput(
              "billToPhone",
              "Bill to Phone Number",
              "tel",
              6,
              false
            )}
            {renderInput(
              "billToEmail",
              "Bill to E-Mail Address",
              "email",
              12,
              false
            )}
            {renderInput("other", "Other", "textarea", 12, false)}
          </Row>
        </Form>
      </Container>
    );
  }
);

export default RequestorInformationForm;
